import { useState, type ReactNode } from 'react';
import { Button } from './Button';

interface MonthDownloadPickerProps {
  /** Button text, e.g. "Zeitbestätigung herunterladen". */
  label: string;
  /** Accessible name of the month field. */
  monthLabel: string;
  downloading: boolean;
  onDownload: (year: number, month: number) => void;
  icon?: ReactNode;
  /** Earliest selectable month as YYYY-MM (e.g. the hire month). */
  min?: string;
  className?: string;
}

/** YYYY-MM of the current local month — the latest month a report can cover. */
function currentMonthValue(): string {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
}

function parseMonthValue(value: string): { year: number; month: number } | null {
  const match = /^(\d{4})-(\d{2})$/.exec(value);
  if (!match) return null;
  const year = Number(match[1]);
  const month = Number(match[2]);
  if (month < 1 || month > 12) return null;
  return { year, month };
}

/** A month field (defaulting to the current month, no future months) next to a download button. */
export function MonthDownloadPicker({
  label,
  monthLabel,
  downloading,
  onDownload,
  icon,
  min,
  className = '',
}: MonthDownloadPickerProps) {
  const max = currentMonthValue();
  const [value, setValue] = useState(max);
  const parsed = parseMonthValue(value);
  // String comparison is safe: both sides are zero-padded YYYY-MM.
  const inRange = parsed !== null && value <= max && (!min || value >= min);

  return (
    <div className={`flex items-center gap-2 ${className}`}>
      <input
        type="month"
        value={value}
        max={max}
        min={min}
        onChange={(e) => setValue(e.target.value)}
        aria-label={monthLabel}
        className="w-36 shrink-0 px-2 py-1.5 rounded-md bg-white border border-slate-200 text-[13px] text-slate-900 tabular-nums focus:outline-none focus:ring-2 focus:ring-[#00A871]/30 focus:border-[#00A871]/50"
      />
      <Button
        variant="secondary"
        className="flex-1"
        disabled={downloading || !inRange}
        loading={downloading}
        onClick={() => parsed && onDownload(parsed.year, parsed.month)}
        leftIcon={icon}
      >
        {label}
      </Button>
    </div>
  );
}
